export type HtmlOutputFormat = 'png' | 'jpg'

export interface HtmlRenderResult {
  blob: Blob
  url: string
  width: number
  height: number
  format: HtmlOutputFormat
}

export interface HtmlRenderCopy {
  emptyHtml: string
  invalidSize: string
  tooLargeCanvas: string
  renderFailed: string
  exportFailed: string
}

const MAX_DIMENSION = 4000
const MAX_AREA = 12000000
const XHTML_NS = 'http://www.w3.org/1999/xhtml'
const BLOCKED_TAGS = new Set(['script', 'iframe', 'object', 'embed', 'link', 'meta', 'base'])

export function clampDimension(value: number): number {
  if (!Number.isFinite(value)) return 1
  return Math.min(MAX_DIMENSION, Math.max(1, Math.round(value)))
}

export function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`
}

export function outputFilename(format: HtmlOutputFormat): string {
  return `html-to-image.${format === 'jpg' ? 'jpg' : 'png'}`
}

export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  anchor.click()
  URL.revokeObjectURL(url)
}

export function buildPreviewDocument(html: string): string {
  const parsed = parseAndSanitize(html)
  const bodyMarkup = parsed.body.innerHTML.trim()
  const headStyles = collectHeadStyles(parsed)

  return `<!doctype html><html><head><meta charset="utf-8"><style>html,body{margin:0;min-height:100%;}body{font-family:Inter,Arial,sans-serif;background:#ffffff;color:#111827;}${headStyles}</style></head><body>${bodyMarkup}</body></html>`
}

export function buildStaticPreviewDocument(bodyMarkup: string): string {
  return `<!doctype html><html><head><meta charset="utf-8"><style>html,body{margin:0;min-height:100%;}body{font-family:Inter,Arial,sans-serif;background:#ffffff;color:#111827;}</style></head><body>${bodyMarkup}</body></html>`
}

export async function renderHtmlToImage(
  html: string,
  width: number,
  height: number,
  format: HtmlOutputFormat,
  copy: HtmlRenderCopy,
): Promise<HtmlRenderResult> {
  if (!html.trim()) {
    throw new Error(copy.emptyHtml)
  }

  const targetWidth = clampDimension(width)
  const targetHeight = clampDimension(height)

  if (targetWidth < 1 || targetHeight < 1) {
    throw new Error(copy.invalidSize)
  }

  if (targetWidth * targetHeight > MAX_AREA) {
    throw new Error(copy.tooLargeCanvas)
  }

  const parsed = parseAndSanitize(html)
  const xhtml = buildForeignObjectHtml(parsed, targetWidth, targetHeight)
  const svg = [
    `<svg xmlns="http://www.w3.org/2000/svg" width="${targetWidth}" height="${targetHeight}" viewBox="0 0 ${targetWidth} ${targetHeight}">`,
    `<foreignObject x="0" y="0" width="100%" height="100%">${xhtml}</foreignObject>`,
    '</svg>',
  ].join('')

  const svgUrl = URL.createObjectURL(new Blob([svg], { type: 'image/svg+xml;charset=utf-8' }))

  try {
    const image = await loadImage(svgUrl, copy.renderFailed)
    const canvas = document.createElement('canvas')
    canvas.width = targetWidth
    canvas.height = targetHeight
    const context = canvas.getContext('2d')

    if (!context) {
      throw new Error(copy.renderFailed)
    }

    if (format === 'jpg') {
      context.fillStyle = '#ffffff'
      context.fillRect(0, 0, targetWidth, targetHeight)
    }

    context.drawImage(image, 0, 0)

    const mimeType = format === 'jpg' ? 'image/jpeg' : 'image/png'
    const blob = await canvasToBlob(
      canvas,
      mimeType,
      copy.exportFailed,
      format === 'jpg' ? 0.92 : undefined,
    )
    const url = URL.createObjectURL(blob)

    return {
      blob,
      url,
      width: targetWidth,
      height: targetHeight,
      format,
    }
  } catch (err) {
    if (err instanceof Error) {
      throw err
    }
    throw new Error(copy.exportFailed)
  } finally {
    URL.revokeObjectURL(svgUrl)
  }
}

function parseAndSanitize(html: string): Document {
  const parsed = new DOMParser().parseFromString(html, 'text/html')
  sanitizeNode(parsed.documentElement)
  return parsed
}

function sanitizeNode(node: Node): void {
  if (node.nodeType === Node.ELEMENT_NODE) {
    const element = node as Element
    const tagName = element.tagName.toLowerCase()

    if (BLOCKED_TAGS.has(tagName)) {
      element.remove()
      return
    }

    for (const attribute of Array.from(element.attributes)) {
      const name = attribute.name.toLowerCase()
      const value = attribute.value.trim().toLowerCase()
      const isFetchedReference =
        name === 'src' ||
        name === 'srcset' ||
        name === 'poster'
      const isExternalHref = name === 'href' && /^(https?:)?\/\//.test(value)

      if (
        name.startsWith('on') ||
        value.startsWith('javascript:') ||
        isExternalHref ||
        (isFetchedReference && !value.startsWith('data:'))
      ) {
        element.removeAttribute(attribute.name)
      }

      if (name === 'style') {
        element.setAttribute(attribute.name, sanitizeCss(attribute.value))
      }
    }

    if (tagName === 'style') {
      element.textContent = sanitizeCss(element.textContent ?? '')
    }
  }

  for (const child of Array.from(node.childNodes)) {
    sanitizeNode(child)
  }
}

function sanitizeCss(css: string): string {
  return css
    .replace(/@import[^;]+;?/gi, '')
    .replace(/url\((?!['"]?data:)[^)]+\)/gi, 'none')
}

function collectHeadStyles(parsed: Document): string {
  return Array.from(parsed.head.querySelectorAll('style'))
    .map((style) => sanitizeCss(style.textContent ?? ''))
    .filter(Boolean)
    .join('\n')
}

function buildForeignObjectHtml(parsed: Document, width: number, height: number): string {
  const wrapper = document.implementation.createHTMLDocument('').createElementNS(XHTML_NS, 'div')
  wrapper.setAttribute('xmlns', XHTML_NS)
  wrapper.setAttribute(
    'style',
    [
      `box-sizing:border-box;width:${width}px;min-height:${height}px;overflow:hidden;`,
      'background:#ffffff;color:#111827;font-family:Inter,Arial,sans-serif;',
    ].join(''),
  )

  const style = document.createElementNS(XHTML_NS, 'style')
  style.textContent = [
    '*,*::before,*::after{box-sizing:border-box;}',
    'body{margin:0;}',
    collectHeadStyles(parsed),
  ].join('\n')
  wrapper.appendChild(style)

  for (const child of Array.from(parsed.body.childNodes)) {
    wrapper.appendChild(child.cloneNode(true))
  }

  return new XMLSerializer().serializeToString(wrapper)
}

function loadImage(url: string, fallbackMessage: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image()
    image.onload = () => resolve(image)
    image.onerror = () => reject(new Error(fallbackMessage))
    image.src = url
  })
}

function canvasToBlob(
  canvas: HTMLCanvasElement,
  type: string,
  fallbackMessage: string,
  quality?: number,
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob)
        } else {
          reject(new Error(fallbackMessage))
        }
      },
      type,
      quality,
    )
  })
}
